// oxlint-disable no-autofocus
'use client';

import { AutoHeightContainer } from '@/components/ui/auto-height-container';
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandInput,
  CommandList,
} from '@/components/ui/command';
import { DropdownMenuShortcut } from '@/components/ui/dropdown-menu';
import useIsMac from '@/hooks/use-is-mac';
import { MagnifyingGlassIcon } from '@heroicons/react/16/solid';
import type { ModelName } from '@pigello/pigello-matrix';
import { useRouter } from 'next/navigation';
import { useCallback, useState } from 'react';
import { useDebounceCallback } from 'usehooks-ts';
import { GlobalSearchCommandGroups } from './components/global-search-command-group';
import { RecentSearches } from './components/recent-searches';
import { RoutesGroup } from './components/routes';
import { SearchGroup } from './components/search-group';
import { SearchableModelsGroup } from './components/searchable-models';
import { ThemeGroup } from './components/theme';
import { useKeyDown } from '@/hooks/use-keydown';
import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { SearchXIcon } from 'lucide-react';

export type Page = 'search' | 'searchable-models' | 'navigation' | 'theme';

export function GlobalSearch({ hiddenInput }: { hiddenInput: boolean }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [asyncSearch, setAsyncSearch] = useState('');
  const [modelName, setModelName] = useState<ModelName>();
  const [pages, setPages] = useState<Page[]>([]);
  const page = pages[pages.length - 1];
  const debouncedSearch = useDebounceCallback(setAsyncSearch, 300);
  const router = useRouter();
  useKeyDown(
    'k',
    (e) => {
      e.preventDefault();
      setOpen((open) => !open);
    },
    { metaKey: true }
  );
  useKeyDown('Escape', (e) => {
    e.preventDefault();
    setOpen(false);
  });
  useKeyDown(
    'b',
    (e) => {
      e.preventDefault();
      router.back();
    },
    { metaKey: true }
  );
  useKeyDown('Backspace', (e) => {
    if (!asyncSearch && !search && open) {
      e.preventDefault();
      setPages((pages) => pages.slice(0, -1));
    }
  });

  const onOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setPages([]);
      setSearch('');
      setAsyncSearch('');
      setModelName(undefined);
    }
    setOpen(open);
  }, []);

  const isMac = useIsMac();
  return (
    <>
      {!hiddenInput && (
        <button
          onClick={() => setOpen(true)}
          className='flex w-full items-center justify-start rounded-sm border border-input bg-muted py-1 pr-1 pl-3 text-sm text-muted-foreground shadow-md'
        >
          <MagnifyingGlassIcon className='mr-2 size-4' />
          Sök i systemet{' '}
          <DropdownMenuShortcut>
            {isMac ? '⌘ + K' : 'ctrl + K'}
          </DropdownMenuShortcut>
        </button>
      )}
      <CommandDialog
        open={open}
        onOpenChange={onOpenChange}
        className='sm:rounded-xl'
      >
        <Command
          shouldFilter={page !== 'search'}
          loop={page !== 'search' && !modelName}
        >
          {page !== 'search' && (
            <CommandInput
              autoFocus
              value={search}
              onValueChange={setSearch}
              placeholder='Sök i systemet...'
            />
          )}
          {page === 'search' && (
            <CommandInput
              autoFocus
              value={search}
              onValueChange={(e) => {
                setSearch(e);
                debouncedSearch(e);
              }}
              placeholder='Sök i systemet...'
            />
          )}
          <AutoHeightContainer>
            <CommandList>
              {page !== 'search' && (
                <CommandEmpty>
                  <Empty>
                    <EmptyHeader>
                      <EmptyMedia variant='icon'>
                        <SearchXIcon />
                      </EmptyMedia>
                      <EmptyTitle>Inga resultat hittades</EmptyTitle>
                    </EmptyHeader>
                  </Empty>
                </CommandEmpty>
              )}
              {!page && (
                <>
                  {!search && (
                    <RecentSearches onClose={() => onOpenChange(false)} />
                  )}
                  <GlobalSearchCommandGroups
                    onClose={() => onOpenChange(false)}
                    setPages={setPages}
                    setSearch={setSearch}
                  />
                </>
              )}
              {page === 'theme' && (
                <ThemeGroup onClose={() => onOpenChange(false)} />
              )}
              {(page === 'searchable-models' || (!page && search !== '')) && (
                <SearchableModelsGroup
                  setPages={setPages}
                  setSearch={setSearch}
                  setModelName={setModelName}
                />
              )}
              {(page === 'navigation' || (!page && search !== '')) && (
                <RoutesGroup onClose={() => onOpenChange(false)} />
              )}
              {page === 'search' && modelName && (
                <SearchGroup
                  modelName={modelName}
                  search={asyncSearch}
                  onClose={() => onOpenChange(false)}
                />
              )}
            </CommandList>
          </AutoHeightContainer>
        </Command>
      </CommandDialog>
    </>
  );
}
